import { App, Form, Input, Modal, Select } from "antd";
import { ApiService } from "api/api.service";
import { Resource } from "dtos/stage.dto";
import { T_MongoId } from "types/_utils.types";
import { useMemo } from "react";
import { TaskStagePicker } from "./TaskStagePicker";
import { AddTaskItemPayload, StructuredTaskItemDto } from "dtos/tasks/task-template.dto";

interface AddTaskItemModalProps {
  open: boolean;
  onClose: () => void;
  templateId: string | undefined;
  groupId: T_MongoId | undefined;
  /** All tasks in this group (structured, for parent/previous pickers) */
  groupTasks: StructuredTaskItemDto[];
  orgCode: string | undefined;
  resource: Resource | undefined;
  parent: T_MongoId | null;
  previous: T_MongoId | null;
}

/**
 * Modal for adding a task (or subtask) to a group.
 *
 * Logic:
 * - If `parent` is selected → `previous` options show subtasks of that parent
 * - If no `parent` → `previous` options show root-level tasks only
 * - `relatedStage` uses TaskStagePicker (returns stage ObjectId)
 */
export const AddTaskItemModal = ({
  open,
  onClose,
  templateId,
  groupId,
  groupTasks,
  orgCode,
  resource,
  parent,
  previous,
}: AddTaskItemModalProps) => {
  const [form] = Form.useForm<AddTaskItemPayload>();
  const { message } = App.useApp();

  const addTaskItem = ApiService.taskTemplate.useAddTaskItem(templateId, groupId);

  const selectedParent = Form.useWatch("parent", form);

  /** Root-level tasks (no parent) — candidates for being a parent */
  const rootTasks = useMemo(
    () => groupTasks.filter((t) => !t.subtasks?.length || true),
    // All root tasks are valid parents; we just need the flat root list
    [groupTasks],
  );

  /**
   * Previous options depend on whether a parent is selected:
   * - With parent: subtasks of that parent task
   * - Without parent: root-level tasks
   */
  const previousOptions = useMemo(() => {
    if (selectedParent) {
      const parent = groupTasks.find((t) => String(t._id) === String(selectedParent));
      return (parent?.subtasks ?? []).map((t) => ({
        label: t.name,
        value: String(t._id),
      }));
    }
    return groupTasks.map((t) => ({
      label: t.name,
      value: String(t._id),
    }));
  }, [selectedParent, groupTasks]);

  const handleFinish = async (values: AddTaskItemPayload) => {
    try {
      await addTaskItem.mutateAsync({
        name: values.name,
        description: values.description || undefined,
        relatedStage: values.relatedStage || undefined,
        parent: values.parent ?? null,
        previous: values.previous ?? null,
        meta: {},
      });
      message.success("Task added");
      form.resetFields();
      onClose();
    } catch {
      message.error("Failed to add task");
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Add Task"
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText="Add Task"
      okButtonProps={{ loading: addTaskItem.isPending }}
      centered
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ parent, previous }}
        style={{ marginTop: 16 }}
      >
        <Form.Item
          name="name"
          label="Task Name"
          rules={[{ required: true, min: 4, message: "Name must be at least 4 characters" }]}
        >
          <Input placeholder="e.g. Verify income documents" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} placeholder="Optional details about what this task involves" />
        </Form.Item>

        <Form.Item
          name="parent"
          label="Parent Task"
          tooltip="Select a parent to make this a subtask (max 1 level deep)."
        >
          <Select
            allowClear
            placeholder="None — root-level task"
            options={rootTasks.map((t) => ({
              label: t.name,
              value: String(t._id),
            }))}
            onChange={() => {
              // Reset previous when parent changes — the sibling list changes
              form.setFieldValue("previous", null);
            }}
          />
        </Form.Item>

        <Form.Item
          name="previous"
          label="Comes After"
          tooltip="The task that comes directly before this one in the sequence."
        >
          <Select
            allowClear
            placeholder="First in sequence"
            options={previousOptions}
            disabled={previousOptions.length === 0}
          />
        </Form.Item>

        <Form.Item name="relatedStage" label="Linked Stage">
          <TaskStagePicker orgCode={orgCode} resource={resource} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
